import {
    AmazonComponent,
    AmazonParsedResult,
    ExcelParseResult,
    useAmazonExcelParser
} from '@/composables/amazonExcelParser';
import { TreeType } from '@/components/Layers/modal/types';
import { ImportLayersElements, LayerElementsMetadata } from '@/typings/layers/layers';
import { IFileParserStrategy } from '@/typings/parsers/externalFileParser';
import { FileParserType } from '@/typings/parsers/fileParserTypes';
import { mmToPixels } from '@/utils/unitsUtils';

export interface CustomilyAmzTemplate {
    name: string;
    metadata: Record<string, unknown>;
    surfaces: Array<{
        id: string;
        name: string;
        baseImageUrl: unknown;
        width: number;
        height: number;
    }>;
    components: Array<{
        id: string;
        type: string;
        columnIndex?: number;
        properties: Record<string, unknown>;
        children?: Array<{
            id: string;
            type: string;
            columnIndex?: number;
            properties: Record<string, unknown>;
        }>;
    }>;
    canvasObjects: Array<{
        id: string;
        type: string;
        columnIndex?: number;
        dimension: {
            width: number;
            height: number;
        };
        position: {
            x: number;
            y: number;
        };
        [key: string]: unknown;
    }>;
    bindings: AmazonParsedResult['bindings'];
}

export class AmazonExcelFileParser implements IFileParserStrategy<AmazonParsedResult> {
    type: FileParserType = 'AMAZON_FILE_XLSX';
    page = 1;
    // Amazon Custom templates typically use millimeters with 300 DPI for production
    private readonly DPI = 300;

    async parse(buffer: ArrayBuffer): Promise<AmazonParsedResult> {
        const { parseExcelToJson, parseAmazonFormat } = useAmazonExcelParser();
        const result: ExcelParseResult<Record<string, unknown>> = await parseExcelToJson(buffer, {
            sheetIndex: 1
        });
        const parsedData = parseAmazonFormat(result.data, result.worksheet);
        return parsedData;
    }

    async extractLayers(result: AmazonParsedResult, importLayers: ImportLayersElements): Promise<ImportLayersElements> {
        const selectedPage = importLayers?.selectedPage || this.page;

        // Transform to Customily template structure
        const customilyTemplate = this.transformToCustomilyTemplate(result);

        // Convert to layers format for the modal
        const elements = this.convertTemplateToLayerElements(customilyTemplate);

        return {
            ...importLayers,
            type: this.type,
            mode: 'LAYERS',
            artWorkThumbnail: importLayers?.artWorkThumbnail || '',
            artworkName: customilyTemplate.name,
            includeDimensions: importLayers?.includeDimensions ?? true,
            selectedPage,
            pages: [
                {
                    pageNumber: 1,
                    name: customilyTemplate.surfaces[0]?.name || 'Page 1',
                    artWorkWidth: Number(customilyTemplate.surfaces[0]?.width) || 400,
                    artWorkHeight: Number(customilyTemplate.surfaces[0]?.height) || 400,
                    elements
                }
            ]
        };
    }

    /**
     * Transform Amazon parsed result to Customily template structure
     */
    transformToCustomilyTemplate(result: AmazonParsedResult) {
        const template = {
            name: (result.template.name as string) || 'Amazon Custom Template',
            metadata: result.template,
            surfaces: this.transformSurfaces(result.surfaces),
            components: this.transformComponents(result.components),
            canvasObjects: this.transformCanvasObjects(result.canvasObjects),
            bindings: result.bindings
        };
        return template;
    }

    private transformSurfaces(surfaces: AmazonParsedResult['surfaces']) {
        return surfaces.map((surface) => {
            const widthValue = this.extractFieldValue(surface.fields, 'width');
            const heightValue = this.extractFieldValue(surface.fields, 'height');

            // Convert from mm to pixels using DPI
            const widthInPixels = typeof widthValue === 'number' ? mmToPixels(widthValue, this.DPI) : 400;
            const heightInPixels = typeof heightValue === 'number' ? mmToPixels(heightValue, this.DPI) : 400;

            return {
                id: surface.id,
                name: surface.type,
                baseImageUrl: this.extractFieldValue(surface.fields, 'baseImage.imageUrl'),
                width: widthInPixels,
                height: heightInPixels
            };
        });
    }

    private transformComponents(components: AmazonComponent[]): CustomilyAmzTemplate['components'] {
        return components.map((component) => {
            const baseComponent: CustomilyAmzTemplate['components'][0] = {
                id: component.id,
                type: component.type,
                columnIndex: component.columnIndex,
                properties: this.fieldsToObject(component.fields)
            };

            // Include children if present (options, fontOptions, colorOptions)
            if (component.children && component.children.length > 0) {
                baseComponent.children = component.children.map((child) => ({
                    id: child.id,
                    type: child.type,
                    columnIndex: child.columnIndex,
                    properties: this.fieldsToObject(child.fields)
                }));
            }

            return baseComponent;
        });
    }

    private transformCanvasObjects(
        canvasObjects: AmazonParsedResult['canvasObjects']
    ): CustomilyAmzTemplate['canvasObjects'] {
        return canvasObjects.map((obj) => {
            const properties = this.fieldsToObject(obj.fields);

            // Extract raw values (in mm)
            const widthMm = properties['dimension.width'] || properties.width;
            const heightMm = properties['dimension.height'] || properties.height;
            const xMm = properties['position.x'] || properties.left || 0;
            const yMm = properties['position.y'] || properties.top || 0;

            // Convert from mm to pixels using DPI
            const widthPx = typeof widthMm === 'number' ? mmToPixels(widthMm, this.DPI) : 500;
            const heightPx = typeof heightMm === 'number' ? mmToPixels(heightMm, this.DPI) : 500;
            const xPx = typeof xMm === 'number' ? mmToPixels(xMm, this.DPI) : widthPx / 2;
            const yPx = typeof yMm === 'number' ? mmToPixels(yMm, this.DPI) : heightPx / 2;

            return {
                id: obj.id,
                type: obj.type,
                columnIndex: obj.columnIndex,
                // Convert dimensions and positions to pixels
                dimension: {
                    width: widthPx,
                    height: heightPx
                },
                position: {
                    x: xPx,
                    y: yPx
                },
                // Keep all other properties
                ...properties
            };
        });
    }

    private fieldsToObject(fields: AmazonComponent['fields']): Record<string, unknown> {
        const obj: Record<string, unknown> = {};
        fields.forEach((field) => {
            obj[field.field] = field.value;
        });
        return obj;
    }

    private extractFieldValue(fields: AmazonComponent['fields'], fieldName: string): unknown {
        const field = fields.find((f) => f.field === fieldName);
        return field?.value;
    }

    /**
     * Convert Customily template to layer elements for modal display
     * Note: Amazon Excel uses top/left coordinates, but Customily/Fabric.js uses center/center origin
     */
    private convertTemplateToLayerElements(template: CustomilyAmzTemplate): LayerElementsMetadata[] {
        const elements: LayerElementsMetadata[] = [];
        let zIndex = 0;

        // Add canvas objects as layer elements
        template.canvasObjects.forEach((canvasObj) => {
            const binding = template.bindings.find((b) => b.canvasObjectId === canvasObj.id);
            const linkedComponent = binding ? template.components.find((c) => c.id === binding.componentId) : null;

            // Convert from top/left origin to center/center origin
            // In Fabric.js, when originX='center' and originY='center', the left/top values represent the center point
            const centerX = canvasObj.position.x + canvasObj.dimension.width / 2;
            const centerY = canvasObj.position.y + canvasObj.dimension.height / 2;

            elements.push({
                width: canvasObj.dimension.width,
                height: canvasObj.dimension.height,
                left: centerX,
                top: centerY,
                visible: true,
                name: canvasObj.type,
                textValue: linkedComponent ? `${canvasObj.type}` : canvasObj.type,
                type: this.determineTreeType(canvasObj.type),
                fontSize: (canvasObj?.['font.fontSize'] as number) || 300,
                multiline: false,
                zIndex: zIndex++,
                // Store original canvas object data
                customData: {
                    canvasObjectId: canvasObj.id,
                    binding,
                    componentId: linkedComponent?.id
                }
            } as LayerElementsMetadata);
        });

        return elements;
    }

    private determineTreeType(canvasObjectType: string): TreeType {
        // Map canvas object types to TreeType enum
        if (canvasObjectType.toLowerCase().includes('image')) {
            return TreeType.FROM_FILE_IMAGE_ELEMENT;
        }
        return TreeType.FROM_FILE_TEXT_ELEMENT;
    }
}
